import { useState, useMemo } from "react";

// null (sin orden, default request)
export type Order = "asc" | "desc" | null;

interface MultiFilters {
  [key: string]: any[];
}

interface UseTableStateProps<T> {
  data: T[];
  initialSortKey: keyof T;
  searchExtractor: (item: T) => string;
}

export function useTableState<T>({ data, initialSortKey, searchExtractor }: UseTableStateProps<T>) {
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState<MultiFilters>({});
  const [orderBy, setOrderBy] = useState<keyof T>(initialSortKey);
  const [order, setOrder] = useState<Order>(null); 

  // Ciclo de ordenamiento: asc -> desc -> null -> asc
  const handleRequestSort = (property: keyof T) => {
    if (orderBy !== property) {
      // Si cambia de columna, empezamos en "asc"
      setOrderBy(property);
      setOrder("asc");
    } else {
      // Si es la misma columna, rotamos el estado
      if (order === "asc") {
        setOrder("desc");
      } else if (order === "desc") {
        setOrder(null);
      } else {
        setOrder("asc");
      }
    }
  };

  const setSpecificFilter = (key: string, values: any[]) => {
    setFilters((prev) => ({ ...prev, [key]: values }));
  };

  const resetFilters = () => {
    setSearch("");
    setFilters({});
    setOrder(null); // limpiar orden al reiniciar filtros
  };

  const hasActiveFilters =
    search.trim().length > 0 ||
    Object.values(filters).some((vals) => vals && vals.length > 0);

  const processedData = useMemo(() => {
    if (!data) return [];

    // 1. Filtrado dinámico
    let result = data.filter((item) => {
      if (search.trim() && !searchExtractor(item).toLowerCase().includes(search.toLowerCase())) {
        return false;
      }

      for (const [key, selectedValues] of Object.entries(filters)) {
        if (selectedValues && selectedValues.length > 0 && !selectedValues.includes((item as any)[key])) {
          return false;
        }
      }
      return true;
    });

    // 2. Ordenamiento condicional
    // Si 'order' es null, saltamos el sort y se devuelve en el orden original de la data
    if (order !== null) {
      result.sort((a, b) => {
        const valueA = a[orderBy];
        const valueB = b[orderBy];

        if (typeof valueA === "string" && typeof valueB === "string") {
          return order === "asc"
            ? valueA.localeCompare(valueB)
            : valueB.localeCompare(valueA);
        }

        if (valueA < valueB) return order === "asc" ? -1 : 1;
        if (valueA > valueB) return order === "asc" ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [data, search, filters, order, orderBy, searchExtractor]);

  return {
    search,
    setSearch,
    filters,
    setSpecificFilter,
    resetFilters,
    hasActiveFilters,
    order,
    orderBy,
    handleRequestSort,
    processedData,
  };
}